import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getLocaleAndFormatDateFromTimeZone } from './getLocaleAndFormatDateFromTimeZone';

dayjs.extend(customParseFormat);

/**
 * Converts the given date and time strings to a formatted string in the Day.js format.
 *
 * @param date - The date string in the format "dd/mm/yyyy".
 * @param time - The time string in the format "hh:mm".
 * @returns The formatted string in the format "yyyy-mm-ddThh:mm:00".
 */
export function convertDateAndTimeToFormatForBackend(date: string, time: string): string {
  const [first, second, third] = date.split('/');
  const formatted = (elm: string) => (elm && elm.length === 1 ? `0${elm}` : elm);
  return `${formatted(first)}/${formatted(second)}/${formatted(third)}-${time}`;
}

/**
 * Formats a given date string into the 'YYYY-MM-DD' format, suitable for backend filters.
 *
 * @param {string} date - The input date string to be formatted. It should be in a format recognized by the `dayjs` library.
 * @returns {string} - The formatted date string in 'YYYY-MM-DD' format.
 *
 * @description
 * This function utilizes the `dayjs` library to parse and format the input date string.
 * It is specifically designed to prepare date strings for backend filtering purposes.
 *
 * @example
 * ```typescript
 * import { formatDateForBackendFilter } from './dateUtils';
 *
 * const formattedDate = formatDateForBackendFilter('2023-10-15T12:00:00Z');
 * console.log(formattedDate); // Output: '2023-10-15'
 * ```
 *
 * @dependencies
 * - `dayjs`: Ensure the `dayjs` library is installed and imported in your project.
 */
export const formatDateForBackendFilter = (date: string): string => {
  const { formatDatePicker } = getLocaleAndFormatDateFromTimeZone();

  return dayjs(date, formatDatePicker).format('YYYY-MM-DD');
};

export const formatDateToPresent = (date: string): string => {
  return dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
};

export const formatDateFromBEToLocalizied = (date: string): string => {
  const { dateFormat } = getLocaleAndFormatDateFromTimeZone();

  return dayjs(date, 'YYYY-MM-DD').format(dateFormat);
};

export const getTimeElapsedSince = (date: string, unit: 'days' | 'hours' | 'minutes'): number => {
  if (unit === 'days') {
    return Math.max(0, dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'days'));
  }
  return dayjs().diff(dayjs(date), unit);
};
